import React, { ReactElement, useState, useEffect } from 'react'
import type { CrossSellResult } from './CrossSell'
import { formatPriceString } from '../../common/helpers/number-formatters'
import CutToSizeGroupsModal from '../CutToSizeGroup/CutToSizeGroupsModal'
import { CrossSellConfiguration } from './types/models'
import { filterUniqueCrossSellConfigs } from './helpers/filterUniqueCrossSellConfigs'

type CrossSellModalProps = {
  crossSellResults: CrossSellResult[];
  onAddCrossSells: () => void;
  onClose: () => void;
  onQuantityChanged: (id: number, quantity: number) => void;
}

const CrossSellModal = ({ crossSellResults, onAddCrossSells, onClose, onQuantityChanged }: CrossSellModalProps): ReactElement => {
  const [disableAddToBasketBtn, setDisableAddToBasketBtn] = useState<boolean>(true)
  const [crossSellConfiguration, setCrossSellConfiguration] = useState<CrossSellConfiguration|null>()

  const heading = () => {
    if (crossSellConfiguration) {
      return crossSellConfiguration.heading
    }
    return (
      <>The job’s not complete <span className="crossSellModal-header-endText">without these...</span></>
    )
  }

  const copy = () => {
    return crossSellConfiguration?.copy || 'Just in case you forgot, you’re more than likely going to need these to get the job done:'
  }

  const shouldShowImage = () => {
    return crossSellConfiguration && crossSellConfiguration.image
  }

  const updateButtonStatus = () => {
    // Inspect the DOM to see if any cut to size length pricing method products have been added. We inspect crossSellResults to see if any basic pricing method products have been added.
    const summaries = document.querySelectorAll('.cutToSize-innerSummary')

    if (crossSellResults.some((result) => result.product.pricing_method !== 'cut_to_size_length' && result.quantity > 0)) {
      setDisableAddToBasketBtn(false)
    } else if (summaries.length < 1) {
      setDisableAddToBasketBtn(true)
    } else {
      setDisableAddToBasketBtn(false)
    }
  }

  const filterAndSetCrossSellConfiguration = () => {
    const uniqueConfigs = filterUniqueCrossSellConfigs(crossSellResults)
    if (uniqueConfigs.length === 1) {
      setCrossSellConfiguration(uniqueConfigs[0])
    } else {
      setCrossSellConfiguration(null)
    }
  }

  useEffect(() => {
    filterAndSetCrossSellConfiguration()
    updateButtonStatus()
  }, [crossSellResults])

  return (
    <div className={shouldShowImage() ? 'crossSellModal-container crossSellModal-container-withImage' : 'crossSellModal-container'}>
      <div className="crossSellModal-content">
        <div>
          <div className="crossSellModal-content-text">
            <h1 className="crossSellModal-header">
              {heading()}
            </h1>
            <p className="crossSellModal-paragraph">{copy()}</p>
          </div>
          <div className="crossSellModal-productListings">
            {crossSellResults.map((result) => (
              <div key={result.product.sku} data-product-sku={result.product.sku} className="crossSellModal-productListing">
                {result.product.pricing_method === 'cut_to_size_length'
                  ? <CutToSizeGroupsModal
                    image={result.product.thumbnail_2x}
                    groups={[result.cut_to_size_rep]}
                    dimension={result.dimension}
                    thickness={result.thickness}
                    updateButtonStatus={updateButtonStatus}
                  />
                  : <>
                    <div className="crossSellModal-productListing-image"><img src={result.product.thumbnail_2x} alt="" width="78" height="72"></img></div>
                    <div className="crossSellModal-productListing-nameContainer">
                      <div className="crossSellModal-productListing-name">
                        {result.product.name}
                      </div>
                    </div>
                    <div className="crossSellModal-productListing-options">
                      <div className="crossSellModal-quantityContainer">
                        <button
                          style={{ width: '30%' }}
                          type="button"
                          className="product-decrement-quantity basket-qty-change"
                          aria-label="decrease"
                          onClick={(): void => {
                            onQuantityChanged(result.product.id, result.quantity - 1)
                          }}
                        />
                        <input
                          className="crossSellModal-qtyInput"
                          value={result.quantity}
                        />
                        <button
                          style={{ width: '30%' }}
                          type="button"
                          className="product-increment-quantity basket-qty-change"
                          aria-label="increase"
                          onClick={(): void => {
                            onQuantityChanged(result.product.id, result.quantity + 1)
                          }}
                        />
                      </div>
                      <div className="crossSellModal-price">
                        { formatPriceString(String(parseFloat(result.product.price_ex_vat) * result.quantity)) }
                      </div>
                    </div>
                  </>
                }
              </div>
            ))}
          </div>
        </div>
        <div className="crossSellModal-acceptDeclineContainer">
          <button type="button" onClick={onAddCrossSells} className="crossSellModal-accept" disabled={disableAddToBasketBtn}>Add to basket</button>
          <button type="button" onClick={onClose} className="crossSellModal-decline">No thanks, let’s continue</button>
        </div>
      </div>
      {shouldShowImage() && (
        <div className="crossSellModal-imageContainer">
          <img alt="Suggested product image" className="crossSellModal-imageContainer-image" src={crossSellConfiguration.image} />
        </div>
      )}
    </div>
  )
}

export default CrossSellModal
